import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { useSelector, useDispatch } from 'react-redux';

import { Layout, Popup } from 'common/components';
import { MenuButton, UserButton } from 'common/containers';
import { history } from 'common/modules/app/router';
import { initialize } from 'common/modules/app/initialize';
import { Principal } from 'common/modules/app/principal';
import { boardState, BoardStatus, setStatus } from 'common/modules/app/board';
import { SasPagePath } from 'common/utils/urls';

import { PlanListsPage } from 'pages/plan-list';
import { PlanDetailPage } from 'pages/plan-detail';
import { PurchasePage } from 'pages/purchase';
import { ErrorPage } from 'pages/errors';
import { DeleteCompletionPage } from 'pages/delete-completion';
import {
  WelcomeBoard,
  SelectLanguageBoard,
  SelectCountryBoard,
  MenuBoard,
  UserBoard,
  BeforePurchaseBoard,
  BeforeCancelBoard,
  LendingUserBoard,
  CompletionBoard,
  DeleteLendingUserBoard,
  AddLendingUserBoard,
  TerminalBoard,
  BeforeIndividualCancelBoard,
  CancellationConfirmationDialog,
  DeviceActivateBoard,
  ActFileDownloadConfirmationBoard,
  ReqFileUploadResultBoard,
  ApiErrorCompletionBoard,
  ReqFileLoadingErrorBoard,
  ReqFileDeleteConfirmationBoard
} from 'pages/board';
import { AppState } from 'pages';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { POST_LENDING_USER_ID } from 'common/modules/domain/lendingUser';

/** PC・タブレットとスマホの画面サイズの閾値 */
export const PC_MIN_WIDTH = 640;
const planListComponent = () => {
  return <PlanListsPage />;
};

const planDetailComponent = () => {
  return <PlanDetailPage />;
};

const purchaseComponent = () => {
  return <PurchasePage />;
};

const errorComponent = () => {
  return <ErrorPage />;
};

const deleteCompletionComponent = () => {
  return <DeleteCompletionPage />;
};

/**
 * 各ページのベースのPresentation Component
 *
 * 初期化処理を行う。
 * Pathに対応したComponentを描画する。
 */
const App: React.FC<Props> = (props: Props) => {
  // スマホ用表示か否かを保持するLocalState
  const [isMobile, setIsMobile] = React.useState<boolean>(false);

  // 初期化イベント
  React.useEffect(() => {
    props.initialize();
  }, []);

  // 初回・破棄時のイベント。ウィンドウのサイズ変更イベントを追加する
  React.useEffect(() => {
    const listener = () => {
      setIsMobile(window.innerWidth < PC_MIN_WIDTH);
    };
    // 表示時のウィンドウサイズ
    listener();
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);

  const routeComponent = (
    <>
      <Switch>
        <Route exact path={SasPagePath.PlanList} render={planListComponent} />
        <Route
          exact
          path={SasPagePath.PlanDetail}
          render={planDetailComponent}
        />
        <Route exact path={SasPagePath.Purchase} render={purchaseComponent} />
        <Route exact path={SasPagePath.Error} render={errorComponent} />
        <Route
          exact
          path={SasPagePath.DeleteCompletion}
          render={deleteCompletionComponent}
        />
        <Redirect to={SasPagePath.PlanList} />
      </Switch>
    </>
  );

  // ルートのメニューボタンのComponent。オーバーレイの上に表示するため、ヘッダとは別に作成
  const topMenuBtn = React.useMemo(
    () =>
      props.isDisplayMenuBtn ? (
        <div className="menuButton">
          <MenuButton data-test="topMenuBtn" withBoard />
        </div>
      ) : null,
    [props.isDisplayMenuBtn]
  );
  // ルートのユーザーボタンのComponent。オーバーレイの上に表示するため、ヘッダとは別に作成
  const topUserBtn = React.useMemo(
    () =>
      props.isDisplayUserBtn ? (
        <div className="userMenuButton">
          <UserButton data-test="topUserBtn" withBoard />
        </div>
      ) : null,
    [props.isDisplayUserBtn]
  );

  return (
    <ConnectedRouter history={history}>
      <Popup
        onClickBackground={props.clickBackground}
        data-test="popup"
        visible={props.isDisplayPopup}
      >
        <WelcomeBoard />
        <SelectLanguageBoard />
        <SelectCountryBoard isMobile={isMobile} />
        <MenuBoard />
        <UserBoard />
        <BeforePurchaseBoard />
        <BeforeCancelBoard />
        <LendingUserBoard />
        <CompletionBoard />
        <DeleteLendingUserBoard />
        <AddLendingUserBoard />
        <TerminalBoard />
        <DeviceActivateBoard />
        <BeforeIndividualCancelBoard />
        <CancellationConfirmationDialog />
        <ActFileDownloadConfirmationBoard />
        <ReqFileUploadResultBoard />
        <ApiErrorCompletionBoard />
        <ReqFileLoadingErrorBoard />
        <ReqFileDeleteConfirmationBoard />
        {topUserBtn}
        {topMenuBtn}
      </Popup>
      <Layout data-test="sas-layout" isMobile={isMobile}>
        {routeComponent}
      </Layout>
    </ConnectedRouter>
  );
};

/**
 * Redux storeからStateを取得して、Componentに渡すpropsを返す
 */
const useStateProps = () => {
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  return {
    board: boardState,
    isLoggedIn: principalState.isLoggedIn
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _board = state.board;
  /** popupの表示有無 */
  const _isDisplay = !(_board.status === BoardStatus.NOT_DISP);
  /** ボタン表示有無 */
  const _isDisplayMenuBtn = _board.status === BoardStatus.MENU_DISP;
  const _isDisplayUserBtn =
    state.isLoggedIn && _board.status === BoardStatus.USER_DISP;

  /** アプリの初期化処理 */
  const onInit = () => {
    dispatch(initialize());
  };

  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );

  /** popup表示時のBackgroundをクリックした際の処理 */
  const clickBackground = () => {
    if (_board.isProcessing) {
      // 処理中はBackgroundをクリックしてもポップアップを閉じない
    } else if (
      _board.status == BoardStatus.ADD_LENDING_USER_DISP ||
      _board.status == BoardStatus.BEFORE_INDIVIDUAL_CANCEL_DISP ||
      _board.status == BoardStatus.DELETE_LENDING_USER_DISP
    ) {
      // 第三者登録、レンタル解除、個別解約画面表示時、第三者の利用状況の画面に遷移
      dispatch(setStatus(BoardStatus.LENDING_USER_DISP));
    } else {
      dispatch(setStatus(BoardStatus.NOT_DISP));
    }
  };

  return {
    isDisplayPopup: _isDisplay,
    isDisplayMenuBtn: _isDisplayMenuBtn,
    isDisplayUserBtn: _isDisplayUserBtn,
    initialize: onInit,
    clickBackground: clickBackground
  };
};

type Props = ReturnType<typeof useMargeProps>;

/**
 * 各ページのベースのContainer Component
 */
export default function ContainerComponent(): JSX.Element {
  const _props = { ...useMargeProps(useStateProps()) };
  return <App {..._props} />;
}
